import React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import type { OutreachHome } from '../outreachTypes';
import { outreachReviewPath } from '../outreachPaths';
import { homeMessages as messages } from './homeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import campaign from '../live/Campaign.module.scss';
import styles from './Home.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HomeTodayHeroProps {
  today: OutreachHome['today'];
  artist: string;
}

/**
 * The hero of the Pitch home (design outreach-v2 i1): today's sends of the artist against the daily
 * rate of the plan, what still goes out today, and "N wait for you" when pitches wait for a review.
 * When the waiting pitches fill the daily rate, a line says that Plug picks nobody new until the
 * review (final review I1).
 */
const HomeTodayHeroComponent: React.FC<HomeTodayHeroProps> = ({ today, artist }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const total = Math.max(today.rate, today.sent);
  const percent = total === 0 ? 0 : Math.min(100, Math.round((today.sent / total) * 100));
  const waitingCampaign = today.reviewCampaignUuid;

  return (
    <section className={styles.hero} data-testid="home-today">
      <div
        className={campaign.ring}
        style={{ '--p': percent } as React.CSSProperties}
        role="img"
        aria-label={intl.formatMessage(messages.todayRing, { sent: today.sent, rate: total })}
        data-testid="home-today-ring"
      >
        <span className={campaign.ringValue} aria-hidden>
          {today.sent}
          <small>/{total}</small>
        </span>
      </div>
      <div className={styles.heroText}>
        <span className={shared.eyebrow}>
          <span className={shared.dot} />
          {intl.formatMessage(messages.todayEyebrow)}
        </span>
        <h2>
          {today.sent > 0
            ? intl.formatMessage(messages.todaySent, { count: today.sent, artist })
            : intl.formatMessage(messages.todayNone, { rate: today.rate, artist })}
        </h2>
        <p>
          {today.goingOut > 0
            ? intl.formatMessage(messages.todayGoingOut, { count: today.goingOut })
            : intl.formatMessage(messages.todayNext)}
        </p>
        {today.reviewBlocksPicks && (
          <p className={shared.small} data-testid="home-today-queue-full">
            {intl.formatMessage(messages.todayQueueFull)}
          </p>
        )}
      </div>
      {today.reviewWaiting > 0 && waitingCampaign !== null && (
        <div className={styles.heroActions}>
          <SMLButton
            size="sm"
            outline
            omitCap
            // eslint-disable-next-line no-void
            onClick={() => void navigate(outreachReviewPath(waitingCampaign))}
            testID="home-today-waiting"
          >
            {intl.formatMessage(messages.todayWaiting, { count: today.reviewWaiting })}
          </SMLButton>
        </div>
      )}
    </section>
  );
};

export const HomeTodayHero = TypedMemo(HomeTodayHeroComponent);
